/**
 * useSearchOperationsClient
 *
 * Client-side search hook that replaces the legacy backend-driven
 * `useSearchOperations`. Runs the Fuse search inside a Web Worker so a
 * keystroke can paint the input even mid-search — the alternative is a
 * synchronous Fuse call against a ~30k-item corpus blocking the main
 * thread for hundreds of ms, freezing the picker's search field every
 * time the debounce fires.
 *
 * The worker is a module-level singleton (`new searchOperationsWorker()`)
 * holding a single `DefaultSearchOperationsService` instance. The corpus
 * is shipped to the worker exactly once per `useAllOperations()` change;
 * keystrokes only postMessage a compact `{term, mode}` payload and
 * receive back operation IDs that the hook resolves locally against the
 * corpus to preserve reference identity for downstream `React.memo`s.
 *
 * Tests and any environment without `Worker` fall back to running the
 * same `DefaultSearchOperationsService` synchronously on the main
 * thread (matches the previous behavior).
 */

import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DefaultSearchOperationsService,
  dedupeByLowestScore,
  getOperationModeFilter,
  operationDisplayKey,
  type ConnectorSearchGroup,
  type SearchMode,
  type SearchOperationResult,
} from '@/services/searchOperationsService';
import type { DiscoveryOperation } from '@/services/operationsService';
import SearchOperationsWorkerCtor from '@/services/searchOperationsWorker?worker';
import type {
  SearchWorkerRequest,
  SearchWorkerResponse,
  WorkerResultGroup,
} from '@/services/searchOperationsWorker';
import { useAllOperations } from './useAllOperations';
import { useDebounce } from './useDebounce';

export type { SearchMode };

/** Debounce window (ms) between keystroke and worker dispatch. Short
 *  enough to feel responsive; long enough to coalesce typing bursts into
 *  a single dispatch on a 30k-item corpus. The worker also drops stale
 *  `result` messages by `requestId` so a mid-burst keystroke can preempt
 *  a still-in-flight search without flashing stale results. */
const SEARCH_DEBOUNCE_MS = 150;
const WORKER_INIT_DEBOUNCE_MS = 500;

/** Minimum characters before we run a Fuse search. 1-char queries against
 *  a 30k corpus produce a near-useless flood of low-relevance hits — even
 *  off the main thread that's wasted worker CPU. Exported so the picker
 *  UI can render an accurate "keep typing" hint instead of a misleading
 *  "no results" empty state. */
export const MIN_SEARCH_LENGTH = 2;

function getEffectiveSearchTerm(searchTerm: string): string {
  const trimmed = searchTerm.trim();
  return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
}

function postWorkerSearch(
  worker: Worker,
  requestId: number,
  term: string,
  mode: SearchMode,
): void {
  const msg: SearchWorkerRequest = {
    type: 'search',
    requestId,
    term,
    mode,
  };
  worker.postMessage(msg);
}

// ---------------------------------------------------------------------------
// Singleton worker
//
// One Worker per app — the corpus is large (~30k items) and reinitializing
// the Fuse index on every picker mount is wasted work. The worker keeps the
// latest corpus snapshot; switching modes (trigger/action/all) is just a
// per-search filter, so no re-init is needed between picker modes.
// ---------------------------------------------------------------------------

let workerSingleton: Worker | null = null;
let workerUnavailable = false;
/** Has the worker ever been handed a corpus snapshot? Module-level so the
 *  first picker mount in a session gets a synchronous init (otherwise the
 *  user could fire a search against an empty worker), while subsequent
 *  page-arrival reinits get coalesced (see the init effect below). */
let workerEverInitialized = false;

const acquireWorker = (): Worker | null => {
  if (workerSingleton) return workerSingleton;
  if (workerUnavailable) return null;
  if (typeof Worker === 'undefined') {
    workerUnavailable = true;
    return null;
  }
  try {
    workerSingleton = new SearchOperationsWorkerCtor();
    return workerSingleton;
  } catch (err) {
    // jsdom (vitest) and some sandboxed iframes don't support module
    // workers — fall back to the main-thread path silently rather than
    // crash the picker. Logged once so prod incidents are still visible.
    workerUnavailable = true;
    console.warn(
      '[useSearchOperationsClient] Web Worker unavailable, falling back to main-thread search',
      err,
    );
    return null;
  }
};

export interface UseSearchOperationsClientResult {
  /** Flat, score-sorted matches. Empty when `searchTerm` is blank. */
  results: SearchOperationResult[];
  /** Same matches grouped by connector, sorted by best score per group. */
  groups: ConnectorSearchGroup[];
  /** True while the prefetch is still bringing in new pages — UI may show
   *  a non-blocking "Loading more connectors…" hint. Search is always
   *  immediately usable against whatever has loaded so far. */
  isLoadingMore: boolean;
  /** Total operations currently indexed (useful for telemetry / empty state copy). */
  indexSize: number;
  /** True for the brief window between a keystroke and the debounced
   *  Fuse run resolving. UIs may use this to keep the previous result
   *  visible (instead of flashing an empty state) while we wait. */
  isPending: boolean;
}

/**
 * Pick the right corpus view for the picker mode, ship the full corpus to
 * the search worker once per snapshot, then dispatch debounced search
 * requests so heavy keystrokes don't block the main thread on a 30k-item
 * Fuse index.
 */
export const useSearchOperationsClient = (
  searchTerm: string,
  mode: SearchMode = 'all',
): UseSearchOperationsClientResult => {
  // Subscribe to the merged corpus once. We ship the full corpus to the
  // worker (not the mode-filtered view) so switching modes never triggers
  // a re-init — the per-search `mode` field handles trigger/action gating
  // inside the worker.
  const all = useAllOperations();
  const corpusData = all.data;

  // Operation lookup table — resolves the IDs returned by the worker back
  // to the original DiscoveryOperation references on the main thread. This
  // preserves identity for downstream `React.memo` consumers.
  const opIndex = useMemo(() => {
    const m = new Map<string, DiscoveryOperation>();
    for (const op of corpusData) m.set(op.id, op);
    return m;
  }, [corpusData]);

  const worker = useMemo(() => acquireWorker(), []);

  // Main-thread fallback (vitest / jsdom / no-Worker environments). Mirrors
  // the previous synchronous behavior so existing tests don't need a worker
  // shim. Only constructed when the worker truly isn't available.
  const fallbackService = useMemo(() => {
    if (worker) return null;
    return new DefaultSearchOperationsService(corpusData);
  }, [worker, corpusData]);

  // Trailing-edge timer debounce. Coalesces a typing burst into one
  // committed term so we don't dispatch a worker message per character.
  const debouncedTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const isDebouncing = debouncedTerm !== searchTerm;

  // Normalize and gate on minimum length BEFORE dispatch so a 1-char
  // query never reaches the worker.
  const effectiveTerm = getEffectiveSearchTerm(debouncedTerm);

  const [searchOutput, setSearchOutput] = useState<{
    term: string;
    results: SearchOperationResult[];
    groups: ConnectorSearchGroup[];
  }>({ term: '', results: [], groups: [] });

  // Per-search request id; the worker echoes it back on `result` messages
  // so we can discard responses for a term the user has already moved on
  // from. Bumped on every dispatch (including the clear path) so stale
  // results are always rejected.
  const searchRequestIdRef = useRef(0);
  const activeWorkerSearchRef = useRef<{ term: string; mode: SearchMode }>({
    term: effectiveTerm,
    mode,
  });

  useEffect(() => {
    activeWorkerSearchRef.current = { term: effectiveTerm, mode };
  }, [effectiveTerm, mode]);

  // Re-init the worker whenever the corpus snapshot changes.
  //
  // The cost of the Fuse rebuild itself lands off the main thread, but
  // the `postMessage` call structured-clones the entire ~30k-item corpus
  // synchronously on the main thread — ~hundreds of ms on a typical
  // machine. Coalescing matters: while the Azure / Custom paginations are
  // streaming in, `corpusData` flips reference on every page arrival, and
  // re-shipping the whole corpus on every flip is what produces the
  // "[Violation] 'input' handler took 1000ms+" spam when the user types
  // during the initial prefetch.
  //
  // First init for the session runs immediately so a search dispatched
  // right after open isn't racing an empty index. Subsequent reinits
  // (page arrivals) debounce 500ms so a burst of paginations collapses
  // into a single re-ship. If the user already has an active search, run it
  // again right after the refreshed init so stale "no results" states heal
  // when late-arriving connector pages add matching operations.
  useEffect(() => {
    if (!worker) return;
    const send = () => {
      const wasInitialized = workerEverInitialized;
      const msg: SearchWorkerRequest = {
        type: 'init',
        requestId: 0,
        operations: corpusData,
      };
      worker.postMessage(msg);
      workerEverInitialized = true;

      const { term, mode: activeMode } = activeWorkerSearchRef.current;
      if (wasInitialized && term) {
        searchRequestIdRef.current += 1;
        postWorkerSearch(worker, searchRequestIdRef.current, term, activeMode);
      }
    };
    if (!workerEverInitialized) {
      send();
      return;
    }
    const handle = setTimeout(send, WORKER_INIT_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [worker, corpusData]);

  // Resolve worker result groups (operation IDs) into ConnectorSearchGroups
  // with real operation references. Done on the main thread to preserve
  // identity for downstream memoization. Also re-dedupes matches by the
  // displayed label as a safety net: workers don't participate in Vite
  // HMR, so a stale worker bundle could return un-collapsed rows after a
  // dev-server restart.
  const resolveGroups = useCallback(
    (workerGroups: WorkerResultGroup[]): ConnectorSearchGroup[] => {
      const resolved: ConnectorSearchGroup[] = [];
      for (const g of workerGroups) {
        const matches: SearchOperationResult[] = [];
        for (const m of g.matches) {
          const op = opIndex.get(m.operationId);
          // Op may have rotated out of the corpus between dispatch and
          // response — skip; the next init heals the index.
          if (!op) continue;
          matches.push({ operation: op, score: m.score, connectorTier: m.connectorTier });
        }
        if (matches.length === 0) continue;
        const deduped = dedupeByLowestScore(
          matches,
          (r) => operationDisplayKey(r.operation),
          (r) => r.score,
        ).sort((a, b) => a.score - b.score);
        resolved.push({
          connectorId: g.connectorId,
          connectorDisplayName: g.connectorDisplayName,
          isCustom: g.isCustom,
          bestScore: g.bestScore,
          bestConnectorTier: g.bestConnectorTier,
          connectorQuality: g.connectorQuality,
          matches: deduped,
          // Post-dedup count keeps the badge consistent with the visible
          // row count even when the worker returned an older totalMatches.
          totalMatches: deduped.length,
        });
      }
      return resolved;
    },
    [opIndex],
  );

  // Shared result commit — flatten, sort, and commit inside a transition
  // so the heavy downstream render of hundreds of SearchConnectorGroupCards
  // stays interruptible. Without the transition the result commit can
  // starve queued input events (the classic "[Violation] 'input' handler
  // took 1000ms+" the moment the user types during a search).
  const commitGroups = useCallback(
    (term: string, groups: ConnectorSearchGroup[]) => {
      const results: SearchOperationResult[] = [];
      for (const g of groups) for (const m of g.matches) results.push(m);
      results.sort((a, b) => a.score - b.score);
      startTransition(() => {
        setSearchOutput({ term, results, groups });
      });
    },
    [],
  );

  // Subscribe to worker `result` messages. Stale responses (requestId
  // mismatch) and non-result messages are dropped.
  useEffect(() => {
    if (!worker) return;
    const onMessage = (event: MessageEvent<SearchWorkerResponse>) => {
      const msg = event.data;
      if (msg.type !== 'result' || msg.requestId !== searchRequestIdRef.current) return;
      commitGroups(msg.term, resolveGroups(msg.groups));
    };
    worker.addEventListener('message', onMessage);
    return () => worker.removeEventListener('message', onMessage);
  }, [worker, resolveGroups, commitGroups]);

  // Dispatch search (worker) or run the synchronous fallback. Either path
  // bumps the request id, so any in-flight worker response from a stale
  // term is discarded the moment a new dispatch happens.
  useEffect(() => {
    searchRequestIdRef.current += 1;
    const requestId = searchRequestIdRef.current;

    if (!effectiveTerm) {
      setSearchOutput((prev) =>
        prev.term === '' ? prev : { term: '', results: [], groups: [] },
      );
      return;
    }

    if (worker) {
      postWorkerSearch(worker, requestId, effectiveTerm, mode);
      return;
    }

    // Main-thread fallback (jsdom / no-Worker environments). setTimeout(0)
    // yields once so queued input events paint before we monopolize the
    // thread again.
    if (!fallbackService) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      if (cancelled) return;
      const groups = fallbackService.searchAndGroup(
        effectiveTerm,
        getOperationModeFilter(mode),
      );
      if (cancelled) return;
      commitGroups(effectiveTerm, groups);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [worker, fallbackService, effectiveTerm, mode, commitGroups]);

  // "In flight" whenever the displayed term hasn't caught up to the
  // debounced term — covers both the debounce window and the worker
  // round-trip. UIs use this to keep the spinner visible instead of
  // flashing the empty state.
  const isRunning = !!effectiveTerm && searchOutput.term !== effectiveTerm;

  return {
    results: effectiveTerm ? searchOutput.results : [],
    groups: effectiveTerm ? searchOutput.groups : [],
    isLoadingMore: all.isLoading,
    indexSize: corpusData.length,
    isPending: isDebouncing || isRunning,
  };
};
